import { windowHelper, url } from '@tencent/ppd-base-libs';
import { JumpEkycParams } from './jump-ekyc';
import { Emv3dsBindWorkflow, Emv3dsWorkflow } from '@tencent/wphk-components-3ds';
const { redirect } = windowHelper;
const { replace } = windowHelper;
const { addUrlParams } = url;

/**
 * 业务跳转
 * @param url
 * @param jumpOption
 */
export function jumpUrl(url: string, jumpOption: JumpOptions) {
  const { isReplace = false, delay = 300 } = jumpOption;
  const newUrl = getJumpUrl(url, jumpOption);
  jumpTo(newUrl, isReplace, delay);
}

/**
 * 实际跳转至指定url
 */
function jumpTo(url: string, isReplace = false, delay = 300) {
  // 默认添加跳转前300ms延时
  if (isReplace) {
    replace(url, delay);
  } else {
    redirect(url, delay);
  }
}

/**
 * 将url参数设置到url中，并返回新的url
 * @param url 业务页面链接
 * @param jumpOption 跳转参数
 * @returns
 */
function getJumpUrl(url: string, jumpOption: JumpOptions): string {
  const { adtag, backUrl, params = {} } = jumpOption;

  const urlParams: Record<string, string> = {
    ADTAG: adtag,
    ...params,
  };
  if (backUrl) {
    urlParams.backUrl = backUrl;
  }
  return addUrlParams(url, urlParams);
}

/** 跳转选项 */
export interface JumpOptions {
  /** 渠道参数 */
  adtag: string;
  /** 回跳地址 */
  backUrl?: string;
  /** 是否replace跳转 */
  isReplace?: boolean;
  /** 跳转延迟 */
  delay?: number;
  /** 其他参数 */
  params?: {
    [key: string]: any
  };
}

/** 跳转ekyc的qrcode页面，长按qrcode跳转ekyc小程序 */
export function goEkycQrcodePage(jumpOption: JumpOptions) {
  jumpUrl('https://hkwallet.moneydata.hk/hkwalletv3/hkwallet-htdocs-common/redirect.shtml?page=ekyc-qrcode', jumpOption);
}

/**
 * 跳转我的优惠券页面
 * @param jumpOption 跳转参数
 */
export function goMyCoupons(jumpOption: JumpOptions) {
  jumpUrl(`${DISCOUNT_CENTER_URL}?page=couponlist`, jumpOption);
}

/**
 * 跳转优惠专区-商户展示券页面
 * @param jumpOption 跳转参数
 */
export function goMySpCoupons(jumpOption: JumpOptions) {
  jumpUrl(`${DISCOUNT_CENTER_URL}?page=displaycouponlist`, jumpOption);
}

/** 跳转cdd页面的参数 */
export type JumpCddOptions = Omit<JumpEkycParams, 'replace'> & {
  /** 从cdd返回前一页的类型，默认是浏览器回退（回退到业务） */
  backtype?: 'back' | 'close'
};

/** 跳转cdd页面，用于拉起ekyc小程序的中转页 */
export function goCddPage(jumpOption: JumpCddOptions) {
  const url = getCddUrl(jumpOption);
  jumpTo(url);
}

/** 获取cdd页面链接 */
export function getCddUrl(jumpOption: JumpCddOptions) {
  // 默认是cdd关闭后，返回前一步
  const backtype = jumpOption.backtype || 'back';
  return getJumpUrl('https://hkwallet.moneydata.hk/fcgi-bin/app/ewallet_bindcard/ewallet_cdd_index.fcgi', {
    adtag: jumpOption?.adtag,
    params: {
      ...jumpOption,
      backtype,
    },
  });
}

/**
 * 跳转edd页面
 * @param jumpOption 跳转参数
 */
export function goEddPage(jumpOption: JumpOptions) {
  jumpUrl('https://hkwallet.moneydata.hk/fcgi-bin/app/ewallet_bindcard/ewallet_edd_index.fcgi', jumpOption);
}

/**
 * 跳转新版本的提现页面
 * @param jumpOption 跳转参数
 */
export function goDrawPage(jumpOption: JumpOptions) {
  jumpUrl('https://hkwallet.moneydata.hk/fcgi-bin/app/ewallet_bindcard/ewallet_fetchcenter_v3.fcgi', jumpOption);
}

/**
 * 跳转到支付管理页面
 * @param jumpOption 跳转参数
 */
export function goPayManagement(jumpOption: JumpOptions) {
  jumpUrl('https://hkwallet.moneydata.hk/fcgi-bin/app/ewallet_bindcard/ewallet_pay_management.fcgi', jumpOption);
}

const DISCOUNT_CENTER_URL = 'https://prom.wechatpay.com.hk/discount-center/coupon_home.shtml';

/**
 * 跳转优惠中心
 * @param jumpOption 跳转参数
 */
export function goDiscountCenter(jumpOption: JumpOptions) {
  jumpUrl(DISCOUNT_CENTER_URL, jumpOption);
}

/**
 * 跳转商户电子优惠券详情页面
 * @param jumpOption 跳转参数
 */
export function goMerchantCouponDetail(jumpOption: { userScid: string } & JumpOptions) {
  const { userScid } = jumpOption;
  return goDiscountCenter({
    ...jumpOption,
    params: {
      ...jumpOption.params,
      page: 'displaycoupondetail',
      userScid,
    },
  });
}

/**
 * 跳转优惠专区-我的礼遇-优惠券-详情页面
 * @param jumpOption 跳转参数
 */
export function goCouponDetail(jumpOption: { userScid: string } & JumpOptions) {
  const { userScid } = jumpOption;
  jumpUrl(`https://hkwallet.moneydata.hk/cgi-bin/app/ewallet_bindcard/ewallet_coupon_index.cgi?page=coupon-detail&discount_center=1&user_coupon_id=${userScid}`, jumpOption);
}


/**
 * 跳转忘记密码页面
 * @param jumpOption 跳转参数
 */
export function goForgotPassword(jumpOption: JumpOptions) {
  jumpUrl('https://hkwallet.moneydata.hk/fcgi-bin/app/ewallet_bindcard/ewallet_pwd_center.fcgi', jumpOption);
}

/**
 * 跳转修改手机号页面
 * @param jumpOption 跳转参数
 */
export function goChangeMobile(jumpOption: JumpOptions) {
  jumpUrl('https://hkwallet.moneydata.hk/fcgi-bin/app/ewallet_bindcard/ewallet_modifymobile_index.fcgi', jumpOption);
}

/**
 * 跳3ds2.0 业务参数类型
 */
export type Emv3dsParams = {
  /** 3ds 2.0生命周期的令牌 */
  token: string,
  /** 3ds 2.0请求来源场景的枚举值 */
  emv3dsWorkflow: Emv3dsWorkflow,
  /** 3ds 2.0成功回跳地址 */
  backUrl: string,
  /** 3ds 2.0失败回跳地址 */
  errorBackUrl?: string,
  /**  3ds 2.0绑卡请求来源场景的枚举值 */
  emv3dsBindWorkflow?: Emv3dsBindWorkflow
};


/**
 * 跳转3ds 2.0首页
 */
export function goEmv3ds(jumpOption: Emv3dsParams & JumpOptions) {
  return jumpUrl('https://hkwallet.moneydata.hk/api/v1/emv/emv3dsindex', {
    ...jumpOption,
    params: {
      ...jumpOption.params,
      token: jumpOption.token,
      emv3ds_workflow: jumpOption.emv3dsWorkflow,
      backUrl: jumpOption.backUrl,
      errorBackUrl: jumpOption.errorBackUrl || '',
      emv3dsBindWorkflow: jumpOption.emv3dsBindWorkflow || '',
    },
  });
}

/**
 * 跳转狂赏印页面 e-stamp
 */
export function goEStamp(jumpOption: JumpOptions) {
  jumpUrl('https://prom.wechatpay.com.hk/e-stamp/index.shtml', jumpOption);
}

/**
 * 跳转端外红包首页 outside-hongbao
 */
export function goOutSiteHongbao(jumpOption: JumpOptions) {
  jumpUrl('https://hb.wechatpay.com.hk/api/hkhb/v1/OsHbHomePage', jumpOption);
}
